import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Button } from "../ui/button";
import { LASERCUT } from "@/constants/testIds";
import { ArrowDown } from "lucide-react";

const LINE1 = "PRESISI CNC.";
const LINE2 = "ESTETIKA UKIRAN.";
const LINE3 = "KARYA LOGAM CUSTOM.";

const MaskedLine = ({ text, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative h-screen w-full overflow-hidden bg-ink">
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0"
      >
        <img
          src="https://static.prod-images.emergentagent.com/jobs/13f79864-2d72-45f5-9e64-8b6b2b57a89d/images/c500317ebfc09b8c316719688f87cde28f9adab93120ef15dd4612d3059c490c.jpeg"
          alt="Bengkel CNC Lasercut"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/50 to-ink" />

      <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-brass mb-6"
        >
          Fabrikasi Seni Logam Custom — Sejak Presisi Menjadi Karya
        </motion.p>

        <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] text-chromed">
          <MaskedLine text={LINE1} delay={0.35} />
          <MaskedLine text={LINE2} delay={0.5} />
          <MaskedLine text={LINE3} delay={0.65} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 max-w-xl text-base md:text-lg text-chromed/70 leading-relaxed"
        >
          Kami mengubah plat besi mentah menjadi gazebo, furniture, BBQ grill, dan wall art
          bernilai desain tinggi — presisi CNC dipadukan estetika ukiran dan finishing
          powder coating tahan cuaca.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Button
            data-testid={LASERCUT.heroCtaConsult}
            onClick={() => scrollTo("konsultasi")}
            size="lg"
            className="rounded-none bg-brass text-ink hover:bg-brass-light font-medium px-8 h-12 tracking-wide"
          >
            Konsultasi Custom
          </Button>
          <Button
            data-testid={LASERCUT.heroCtaCatalog}
            onClick={() => scrollTo("katalog")}
            size="lg"
            variant="outline"
            className="rounded-none border-chromed/30 text-chromed hover:bg-chromed/10 hover:text-chromed px-8 h-12 tracking-wide bg-transparent"
          >
            Lihat Katalog
          </Button>
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-8 right-8 md:right-24 text-chromed/50 z-10"
      >
        <ArrowDown size={22} />
      </motion.div>
    </section>
  );
};
